import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save, ExternalLink, Upload, X } from "lucide-react";

const DEFAULT_CATEGORIES = ["General", "Guides", "Design", "BIM & Tech", "Investment", "Sustainability", "Case Studies", "News"];

const EMPTY = {
  title: "", slug: "", excerpt: "", body_html: "",
  cover_image_url: "", gallery_images: [],
  category: "Guides", tags: [], featured: false,
  status: "draft", published_at: "",
  seo_title: "", meta_description: "", keywords: [],
  og_image_url: "", canonical_url: "",
  author_name: "", author_id: "", author_avatar: "",
};

// Quill toolbar — matches the "professional knowledge center" scope.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["code-block"],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header","bold","italic","underline","strike","blockquote","list","indent","link","image","video","code-block"];

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const AdminBlogEditor = ({ mode = "create" }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [tagInput, setTagInput] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const coverRef = useRef(null);
  const ogRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/blog/posts/${id}`);
        setForm({ ...EMPTY, ...data, tags: data.tags || [], keywords: data.keywords || [], gallery_images: data.gallery_images || [] });
        setSlugTouched(true);
      } catch (e) {
        toast.error("Post not found");
        navigate("/admin/blog");
      } finally { setLoading(false); }
    })();
  }, [id, mode, navigate]);

  // Auto-suggest slug from title until user edits it.
  useEffect(() => {
    if (!slugTouched && mode === "create") set("slug", slugify(form.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const readingTime = useMemo(() => {
    const words = (form.body_html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  }, [form.body_html]);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || form.tags.includes(t)) return;
    set("tags", [...form.tags, t]);
    setTagInput("");
  };
  const addKw = () => {
    const k = kwInput.trim();
    if (!k || form.keywords.includes(k)) return;
    set("keywords", [...form.keywords, k]);
    setKwInput("");
  };

  const uploadImage = async (files, target) => {
    const f = (files || [])[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(target, data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  const save = async (nextStatus) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.body_html || form.body_html === "<p><br></p>") { toast.error("Article body is empty"); return; }
    setSaving(true);
    try {
      const payload = { ...form, status: nextStatus || form.status };
      const url = mode === "edit" ? `/admin/blog/posts/${id}` : "/admin/blog/posts";
      const req = mode === "edit" ? api.put : api.post;
      const { data } = await req(url, payload);
      toast.success(mode === "edit" ? "Post updated" : "Post created");
      if (mode === "create") navigate(`/admin/blog/${data.id}/edit`);
      else setForm({ ...EMPTY, ...data, tags: data.tags || [], keywords: data.keywords || [], gallery_images: data.gallery_images || [] });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout title="Blog post"><div className="py-24 text-center text-muted-foreground">Loading…</div></AdminLayout>;

  return (
    <AdminLayout
      title={mode === "edit" ? "Edit post" : "New post"}
      actions={
        <div className="flex gap-2">
          {form.slug && form.status === "published" && (
            <Link to={`/blog/${form.slug}`} target="_blank" className="inline-flex items-center px-3 h-10 text-xs uppercase tracking-widest border border-border">
              <ExternalLink size={13} className="mr-1.5" /> View
            </Link>
          )}
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn" className="rounded-none">
            <Save size={14} className="mr-2" /> Save draft
          </Button>
          <Button onClick={() => save("published")} disabled={saving} data-testid="save-publish-btn" className="rounded-none bg-accent hover:bg-accent/90 text-neutral-900">
            {saving ? "Saving…" : (form.status === "published" ? "Update" : "Publish")}
          </Button>
        </div>
      }
    >
      <Link to="/admin/blog" className="inline-flex items-center text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={12} className="mr-1" /> Back to posts
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-border bg-card p-6 space-y-4">
            <div>
              <Label className="overline text-muted-foreground">Title</Label>
              <Input data-testid="blog-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="How to choose the right floor plan" className="rounded-none h-12 mt-2 text-lg" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">URL slug</Label>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">/blog/</span>
                <Input data-testid="blog-slug-input" value={form.slug} onChange={(e) => { set("slug", slugify(e.target.value)); setSlugTouched(true); }} placeholder="how-to-choose-the-right-floor-plan" className="rounded-none h-10 font-mono text-sm" />
              </div>
              <div className="text-xs text-muted-foreground mt-1">Auto-generated from title; edit for a custom URL.</div>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Excerpt</Label>
              <Textarea data-testid="blog-excerpt-input" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="A 1-3 sentence teaser used on cards and social shares." rows={2} className="rounded-none mt-2" />
              <div className="text-xs text-muted-foreground mt-1">{(form.excerpt || "").length} / 180 chars recommended</div>
            </div>
          </div>

          <div className="border border-border bg-card p-6">
            <Label className="overline text-muted-foreground">Article body</Label>
            <div className="mt-3" data-testid="blog-body-editor">
              <ReactQuill
                theme="snow"
                value={form.body_html}
                onChange={(html) => set("body_html", html)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your article…"
              />
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              ~{readingTime} min read · {(form.body_html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length} words
            </div>
          </div>

          {/* SEO */}
          <div className="border border-border bg-card p-6 space-y-4">
            <div className="overline text-accent">SEO</div>
            <div>
              <Label className="text-xs">SEO title (defaults to article title)</Label>
              <Input data-testid="blog-seo-title-input" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="Choose the Right Floor Plan | Dream Home Engineers" className="rounded-none h-10 mt-1" />
              <div className="text-xs text-muted-foreground mt-1">{(form.seo_title || form.title || "").length} / 60 chars recommended</div>
            </div>
            <div>
              <Label className="text-xs">Meta description (defaults to excerpt)</Label>
              <Textarea data-testid="blog-meta-desc-input" value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} rows={2} className="rounded-none mt-1" />
              <div className="text-xs text-muted-foreground mt-1">{(form.meta_description || form.excerpt || "").length} / 160 chars recommended</div>
            </div>
            <div>
              <Label className="text-xs">Keywords</Label>
              <div className="flex gap-2 mt-1">
                <Input value={kwInput} onChange={(e) => setKwInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKw())} placeholder="Add keyword and press Enter" className="rounded-none h-10" data-testid="blog-kw-input" />
                <Button type="button" onClick={addKw} className="rounded-none h-10">Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.keywords.map((k, i) => (
                  <span key={i} data-testid={`blog-kw-${i}`} className="px-2 py-1 text-xs bg-neutral-100 flex items-center gap-1.5">
                    {k}
                    <button onClick={() => set("keywords", form.keywords.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-rose-600"><X size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Open Graph image (defaults to cover image)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.og_image_url} onChange={(e) => set("og_image_url", e.target.value)} placeholder="Paste URL or upload" className="rounded-none h-10 flex-1" data-testid="blog-og-input" />
                <input ref={ogRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files, "og_image_url")} />
                <Button type="button" onClick={() => ogRef.current?.click()} variant="outline" className="rounded-none h-10"><Upload size={14} /></Button>
              </div>
              {form.og_image_url && <img src={form.og_image_url} alt="" className="mt-2 h-24 object-cover border border-border" />}
            </div>
            <div>
              <Label className="text-xs">Canonical URL (leave empty for default)</Label>
              <Input data-testid="blog-canonical-input" value={form.canonical_url} onChange={(e) => set("canonical_url", e.target.value)} placeholder="https://plans.dreamhomeengineers.com/blog/…" className="rounded-none h-10 mt-1 font-mono text-sm" />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <div className="border border-border bg-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="overline text-muted-foreground">Status</Label>
              <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 ${form.status === "published" ? "bg-emerald-100 text-emerald-800" : form.status === "scheduled" ? "bg-amber-100 text-amber-800" : "bg-neutral-100"}`}>{form.status}</span>
            </div>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="blog-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            {(form.status === "scheduled" || form.status === "published") && (
              <div>
                <Label className="text-xs">{form.status === "scheduled" ? "Scheduled publish date" : "Published at"}</Label>
                <Input data-testid="blog-publish-date-input" type="datetime-local" value={(form.published_at || "").slice(0, 16)} onChange={(e) => set("published_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className="rounded-none h-10 mt-1" />
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <Label className="text-xs">Featured on home / blog</Label>
              <Switch data-testid="blog-featured-switch" checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
            </div>
          </div>

          <div className="border border-border bg-card p-6 space-y-4">
            <Label className="overline text-muted-foreground">Cover image</Label>
            <div className="flex gap-2">
              <Input value={form.cover_image_url} onChange={(e) => set("cover_image_url", e.target.value)} placeholder="Paste URL or upload" className="rounded-none h-10 flex-1" data-testid="blog-cover-input" />
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files, "cover_image_url")} />
              <Button type="button" onClick={() => coverRef.current?.click()} variant="outline" className="rounded-none h-10"><Upload size={14} /></Button>
            </div>
            {form.cover_image_url && <img src={form.cover_image_url} alt="" className="aspect-video w-full object-cover border border-border" />}
          </div>

          <div className="border border-border bg-card p-6 space-y-4">
            <div>
              <Label className="overline text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="blog-category-select" className="rounded-none mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEFAULT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag and press Enter" className="rounded-none h-10" data-testid="blog-tag-input" />
                <Button type="button" onClick={addTag} className="rounded-none h-10">Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.tags.map((t, i) => (
                  <span key={i} data-testid={`blog-tag-${i}`} className="px-2 py-1 text-xs bg-neutral-100 flex items-center gap-1.5">
                    #{t}
                    <button onClick={() => set("tags", form.tags.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-rose-600"><X size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="border border-border bg-card p-6 space-y-4">
            <Label className="overline text-muted-foreground">Author</Label>
            <Input data-testid="blog-author-name-input" value={form.author_name} onChange={(e) => set("author_name", e.target.value)} placeholder="Display name (defaults to your name)" className="rounded-none h-10" />
            <Input data-testid="blog-author-avatar-input" value={form.author_avatar} onChange={(e) => set("author_avatar", e.target.value)} placeholder="Avatar image URL (optional)" className="rounded-none h-10" />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogEditor;
